import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import AskRayFuel from "@/components/AskRayFuel";
import GreenKmSavedCard from "@/components/GreenKmSavedCard";
import LockedFeature from "@/components/LockedFeature";
import { useEntitlements } from "@/context/EntitlementsContext";
import { Sparkles, Loader2, AlertTriangle, RefreshCcw, Calendar, Wrench, Gauge, Copy, Activity, Lock } from "lucide-react";
import { toast } from "sonner";

const badgeCls = "inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 tracking-wide";

export default function Insights() {
  const { flag } = useEntitlements();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [insights, setInsights] = useState(null);
  const [insLoading, setInsLoading] = useState(false);
  const [annual, setAnnual] = useState(null);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => {
      setVehicles(data);
      if (data.length && !vehicleId) setVehicleId(data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canInsights = flag("ai_insights");
  const canAnnual = flag("ai_annual_summary");
  const canAsk = flag("ai_ask");

  const loadInsights = async () => {
    if (!vehicleId) return;
    setInsLoading(true); setInsights(null);
    try {
      const { data } = await api.get(`/ai/insights/${vehicleId}`);
      setInsights(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInsLoading(false);
    }
  };

  const loadAnnual = async () => {
    if (!vehicleId) return;
    setAnnualLoading(true); setAnnual(null);
    try {
      const { data } = await api.get(`/ai/annual-summary/${vehicleId}?year=${year}`);
      setAnnual(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAnnualLoading(false);
    }
  };

  const selectedVehicle = useMemo(() => vehicles.find((v) => v.id === vehicleId), [vehicles, vehicleId]);
  const scoreColor = (s) => (s >= 80 ? "text-emerald-400" : s >= 50 ? "text-amber-400" : "text-red-400");

  return (
    <div className="space-y-6" data-testid="insights-page">
      <div>
        <div className="ft-overline text-xs">RAYFUEL AI</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter flex items-center gap-3">
          <Sparkles className="text-[color:var(--accent,#FF5A1F)]" size={32} /> Insights
        </h1>
        <p className="text-sm text-[color:var(--text-secondary,#A3A3A3)] mt-1">
          Every answer is grounded in your own tenant records. Nothing invented.
        </p>
      </div>

      {/* Ask RayFuel */}
      {canAsk ? (
        <AskRayFuel />
      ) : (
        <LockedFeature flag="ai_ask" title="Ask RayFuel" note="Upgrade to enable natural-language questions grounded in your fleet." />
      )}

      {/* Vehicle picker */}
      <div className="border border-[color:var(--border-line,#262626)] bg-[color:var(--bg-surface,#121212)] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <label className="ft-overline text-[10px] mb-1 block">VEHICLE FOR AI INSIGHTS</label>
          <select
            className="w-full bg-[color:var(--bg-elevated,#1A1A1A)] border border-[color:var(--border-line,#262626)] px-3 py-2.5 text-sm"
            value={vehicleId}
            onChange={(e) => { setVehicleId(e.target.value); setInsights(null); setAnnual(null); }}
            data-testid="insights-vehicle-picker"
          >
            {vehicles.length === 0 && <option value="">No vehicles yet</option>}
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.number} — {v.name}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadInsights}
            disabled={!canInsights || !vehicleId || insLoading}
            className="bg-[color:var(--accent,#FF5A1F)] hover:bg-[color:var(--accent-hover,#E04F1A)] text-[color:var(--accent-fg,#000)] font-bold py-2.5 px-4 text-sm flex items-center gap-2 disabled:opacity-40"
            data-testid="insights-run-btn"
          >
            {insLoading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCcw size={14} />}
            {canInsights ? "Analyse vehicle" : "Analyse (Pro)"}
          </button>
        </div>
      </div>

      {/* Insight cards */}
      {insLoading && (
        <div className="text-sm text-[color:var(--text-secondary,#A3A3A3)] flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Running deterministic + AI analysis…
        </div>
      )}
      {!canInsights && !insights && (
        <LockedFeature flag="ai_insights" title="AI Insights" note="Upgrade to enable duplicate-refuel, wear + anomaly detection with plain-english explanations." />
      )}
      {insights && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-[color:var(--border-line,#262626)] bg-[color:var(--bg-surface,#121212)] p-5" data-testid="insight-health-card">
            <div className="flex items-center gap-2">
              <Activity size={18} className={scoreColor(insights.health_score || 0)} />
              <span className="ft-overline text-xs">VEHICLE HEALTH</span>
            </div>
            <div className={`font-display text-6xl font-black tracking-tighter mt-1 ${scoreColor(insights.health_score || 0)}`}>
              {insights.health_score ?? "—"}
            </div>
            <div className="text-sm text-[color:var(--text-secondary,#A3A3A3)] mt-2">{insights.health_explanation}</div>
          </div>

          <div className="border border-[color:var(--border-line,#262626)] bg-[color:var(--bg-surface,#121212)] p-5" data-testid="insight-trend-card">
            <div className="flex items-center gap-2">
              <Gauge size={18} className="text-[color:var(--accent,#FF5A1F)]" />
              <span className="ft-overline text-xs">COST TREND</span>
              <span className={`${badgeCls} ${insights.cost_trend === "worsening" ? "bg-red-500/20 text-red-400" : insights.cost_trend === "improving" ? "bg-emerald-500/20 text-emerald-400" : "bg-white/10 text-white"}`}>
                {(insights.cost_trend || "unknown").toUpperCase()}
              </span>
            </div>
            <div className="text-sm text-[color:var(--text-secondary,#A3A3A3)] mt-2">{insights.cost_trend_note}</div>
            {insights.service_interval_suggestion && (
              <div className="mt-3 text-xs border-t border-[color:var(--border-line,#262626)] pt-3 text-[color:var(--text-tertiary,#737373)]">
                <strong>Service interval: </strong>{insights.service_interval_suggestion}
              </div>
            )}
          </div>

          <InsightList
            testId="insight-anomalies"
            icon={<AlertTriangle size={16} className="text-amber-400" />}
            title="Mileage anomalies"
            empty="No unusual km/l detected."
            items={insights.mileage_anomalies || []}
            render={(a) => `${a.date || "—"} · ${a.km_per_l ?? "?"} km/l — ${a.note || ""}`}
          />
          <InsightList
            testId="insight-duplicates"
            icon={<Copy size={16} className="text-blue-400" />}
            title="Possible duplicate refuels"
            empty="No duplicates found."
            items={insights.duplicate_refuels || []}
            render={(d) => `${(d.ids || []).slice(0, 2).map((x) => x.slice(0, 6)).join(", ")} — ${d.reason || ""}`}
          />
          <InsightList
            testId="insight-repeats"
            icon={<RefreshCcw size={16} className="text-rose-400" />}
            title="Repeated repairs"
            empty="No repeat repairs seen."
            items={insights.repeated_repairs || []}
            render={(r) => `${r.component} · ${r.count}× · every ~${r.months_between ?? "?"} mo — ${r.note || ""}`}
          />
          <InsightList
            testId="insight-wear"
            icon={<Wrench size={16} className="text-orange-400" />}
            title="Premature wear signals"
            empty="No premature wear detected."
            items={insights.premature_wear || []}
            render={(w) => `${w.component} — ${w.evidence || ""}`}
          />
        </div>
      )}

      {/* Annual summary */}
      <div className="border border-[color:var(--border-line,#262626)] bg-[color:var(--bg-surface,#121212)]" data-testid="annual-summary-card">
        <div className="px-5 py-3 border-b border-[color:var(--border-line,#262626)] flex items-center gap-3">
          <Calendar size={16} className="text-[color:var(--accent,#FF5A1F)]" />
          <span className="font-display text-lg font-black tracking-tight">Annual vehicle summary</span>
          <input
            type="number"
            value={year} onChange={(e) => setYear(parseInt(e.target.value || `${new Date().getFullYear()}`, 10))}
            className="ml-auto w-24 bg-[color:var(--bg-elevated,#1A1A1A)] border border-[color:var(--border-line,#262626)] px-2 py-1.5 text-sm"
            data-testid="annual-summary-year"
          />
          <button
            onClick={loadAnnual}
            disabled={!canAnnual || !vehicleId || annualLoading}
            className="text-sm font-semibold px-3 py-1.5 bg-[color:var(--accent,#FF5A1F)] hover:bg-[color:var(--accent-hover,#E04F1A)] text-[color:var(--accent-fg,#000)] disabled:opacity-40"
            data-testid="annual-summary-run"
          >
            {annualLoading ? <Loader2 className="animate-spin inline" size={14} /> : canAnnual ? "Generate" : "Pro"}
          </button>
        </div>
        {!canAnnual ? (
          <div className="p-5">
            <LockedFeature flag="ai_annual_summary" title="Annual summary" note="Available on the Pro plan — an AI-written recap of your year." />
          </div>
        ) : annual ? (
          <div className="p-5 text-sm text-[color:var(--text-secondary,#A3A3A3)] whitespace-pre-wrap leading-relaxed" data-testid="annual-summary-body">
            {annual.markdown}
          </div>
        ) : (
          <div className="p-5 text-sm text-[color:var(--text-tertiary,#737373)]">
            Pick a year and press Generate — RayFuel reads the year's records and writes a plain-english recap.
          </div>
        )}
      </div>

      {/* Green km saved */}
      <GreenKmSavedCard />
    </div>
  );
}

function InsightList({ icon, title, items, empty, render, testId }) {
  return (
    <div className="border border-[color:var(--border-line,#262626)] bg-[color:var(--bg-surface,#121212)] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="ft-overline text-xs">{title.toUpperCase()}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-[color:var(--text-tertiary,#737373)]">{empty}</div>
      ) : (
        <ul className="space-y-1 text-sm text-[color:var(--text-secondary,#A3A3A3)]">
          {items.slice(0, 6).map((it, i) => <li key={i}>• {render(it)}</li>)}
        </ul>
      )}
    </div>
  );
}
